import React, { useEffect, useState } from 'react';
import { useApp } from '@/context/AppContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import api, { errMsg } from '@/lib/api';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2, Lock, RotateCcw, Copy, Search, CornerDownRight } from 'lucide-react';

const uid = () => (crypto?.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);

const toRow = (g) => ({
  key: g.id, id: g.id, sr: g.sr_no || '', name: g.product || '', category: g.category || '',
  code: g.barcode || '', price: g.price != null ? String(g.price) : '',
  cost: g.cost != null ? String(g.cost) : '', store_id: g.store_id,
  status: g.status, locked: g.status !== 'AVAILABLE', _delete: false, _new: false,
});

export default function PurchaseItemsEditor({ open, onOpenChange, purchaseId, garments = [], onSaved }) {
  const { stores } = useApp();
  const defaultStore = stores[0]?.id || '';
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const letter = (i) => { let s = ''; do { s = String.fromCharCode(65 + (i % 26)) + s; i = Math.floor(i / 26) - 1; } while (i >= 0); return s; };
  const expand = (base, n) => Array.from({ length: n }, (_, i) => base + letter(i));

  useEffect(() => {
    if (open) setRows(garments.map(toRow));
  }, [open, garments]);

  const setCell = (key, field, value) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, [field]: value } : r)));
  const toggleDelete = (key) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, _delete: !r._delete } : r)));
  const removeNew = (key) => setRows((rs) => rs.filter((r) => r.key !== key));
  const newBlank = (cat = '') => ({
    key: uid(), id: null, sr: '', name: '', category: cat, code: '', price: '', cost: '',
    store_id: defaultStore, status: 'AVAILABLE', locked: false, _delete: false, _new: true,
  });
  const addRow = () => setRows((rs) => [...rs, newBlank()]);
  const insertAfter = (key) => setRows((rs) => {
    const i = rs.findIndex((r) => r.key === key);
    if (i === -1) return [...rs, newBlank()];
    const base = newBlank(rs[i].category || '');
    return [...rs.slice(0, i + 1), base, ...rs.slice(i + 1)];
  });
  // "P01*10" / "P01 x10" in a NEW row's code → expand into 10 coded rows (P01A…P01J).
  const onCodeKeyDown = (e, r) => {
    if (e.key !== 'Enter' || r.id) return; // only new rows can multiply
    const m = String(r.code || '').trim().match(/^(.+?)\s*[*xX×]\s*(\d+)$/);
    if (!m) return;
    e.preventDefault();
    const base = m[1].trim().toUpperCase();
    const n = Math.max(1, parseInt(m[2], 10) || 1);
    if (n <= 1) { setCell(r.key, 'code', base); return; }
    const clones = expand(base, n).map((c) => ({ ...newBlank(r.category), sr: r.sr, name: r.name, price: r.price, cost: r.cost, store_id: r.store_id, code: c }));
    setRows((rs) => { const i = rs.findIndex((x) => x.key === r.key); return i === -1 ? rs : [...rs.slice(0, i), ...clones, ...rs.slice(i + 1)]; });
    toast.success(`Created ${n} rows: ${clones[0].code} … ${clones[clones.length - 1].code}`);
  };
  // Auto next-code for a NEW row when a category is set (never overwrites a typed code).
  const suggestCode = async (r, category) => {
    const cat = (category || '').trim();
    if (!cat || r.id || (r.code || '').trim()) return;
    try {
      const info = (await api.get('/catalog/next-code', { params: { category: cat } })).data.data;
      if (info?.next_code) setCell(r.key, 'code', info.next_code);
    } catch (e) { /* no suggestion available */ }
  };

  const storeName = (id) => stores.find((s) => s.id === id)?.name || '—';

  const copyText = async (text) => {
    const val = String(text ?? '');
    if (!val.trim()) { toast.error('Nothing to copy'); return; }
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(val);
      else {
        const ta = document.createElement('textarea');
        ta.value = val; ta.style.position = 'fixed'; ta.style.opacity = '0';
        document.body.appendChild(ta); ta.select(); document.execCommand('copy'); ta.remove();
      }
      toast.success(`Copied: ${val.length > 28 ? val.slice(0, 28) + '…' : val}`);
    } catch { toast.error('Copy failed'); }
  };
  const copyRow = (r) => copyText([r.sr, r.name, r.category, r.code, r.price, r.cost, storeName(r.store_id)].map((x) => String(x ?? '')).join('\t'));
  const CopyBtn = ({ value, testid }) => (value ? (
    <button type="button" tabIndex={-1} onMouseDown={(e) => e.preventDefault()} onClick={() => copyText(value)}
      data-testid={testid} title="Copy"
      className="absolute right-1 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground opacity-0 transition-opacity group-hover/cell:opacity-100 group-focus-within/cell:opacity-100 hover:text-foreground">
      <Copy className="h-3 w-3" />
    </button>
  ) : null);

  const save = async () => {
    const editable = rows.filter((r) => !r.locked && !r._delete && (r._new ? (r.name || r.code) : true));
    // Basic validation on all rows that will be written
    for (const r of editable) {
      if (!r.name.trim() || !r.code.trim()) { toast.error('Every item needs a name and an item code.'); return; }
      if (!r.price || Number(r.price) <= 0) { toast.error(`Enter a valid sale price for "${r.code}".`); return; }
      if (!r.store_id) { toast.error(`Pick a store for "${r.code}".`); return; }
    }
    const edits = rows.filter((r) => r.id && !r._new && !r.locked && !r._delete).map((r) => ({
      id: r.id, product: r.name.trim(), category: r.category.trim(), barcode: r.code.trim().toUpperCase(),
      price: Number(r.price), cost: r.cost === '' ? null : Number(r.cost), store_id: r.store_id, sr_no: r.sr,
    }));
    const removes = rows.filter((r) => r.id && !r._new && !r.locked && r._delete).map((r) => r.id);
    const adds = rows.filter((r) => r._new && (r.name || r.code)).map((r) => ({
      product: r.name.trim(), category: r.category.trim(), barcode: r.code.trim().toUpperCase(),
      price: Number(r.price || 0), cost: r.cost === '' ? null : Number(r.cost), store_id: r.store_id, sr_no: r.sr,
    }));
    if (!edits.length && !removes.length && !adds.length) { toast.error('Nothing to save yet.'); return; }
    setSaving(true);
    try {
      const r = await api.put(`/purchases/${purchaseId}/items`, { edits, removes, adds });
      const d = r.data.data;
      const bits = [];
      if (d.updated) bits.push(`${d.updated} updated`);
      if (d.added) bits.push(`${d.added} added`);
      if (d.removed) bits.push(`${d.removed} removed`);
      if (d.locked) bits.push(`${d.locked} locked skipped`);
      toast.success(`Items saved${bits.length ? ` · ${bits.join(', ')}` : ''}`);
      onSaved?.();
      onOpenChange(false);
    } catch (e) { toast.error(errMsg(e), { duration: 8000 }); }
    finally { setSaving(false); }
  };

  const cell = (r, field, extra = '', props = {}) => (
    <div className="group/cell relative">
      <Input value={r[field]} onChange={(e) => setCell(r.key, field, e.target.value)} disabled={r.locked || r._delete}
        data-testid={`pie-${field}-${r.key}`}
        className={`h-9 rounded-md border-transparent pr-6 focus:border-input ${extra}`} {...props} />
      <CopyBtn value={r[field]} testid={`pie-copy-${field}-${r.key}`} />
    </div>
  );

  const pendingCount = rows.filter((r) => (r._new && (r.name || r.code)) || (r._delete && !r.locked) || (!r._new && !r.locked && !r._delete)).length;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!saving) onOpenChange(v); }}>
      <DialogContent className="max-w-[98vw] max-h-[92vh] overflow-y-auto"
        onPointerDownOutside={(e) => e.preventDefault()} onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Edit items</DialogTitle>
          <DialogDescription>
            Change any detail, delete items, or add new ones to this purchase. Items already sold, transferred or
            returned are <b>locked</b> and can&rsquo;t be changed here.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2">
          <div className="relative flex-1 sm:max-w-xs">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Find code / name…" className="h-9 rounded-lg pl-8 text-sm" data-testid="pie-search" />
          </div>
          <span className="text-xs text-muted-foreground">Tip: type <b>P01*10</b> in a new row&rsquo;s code to make 10 rows.</span>
        </div>

        <div className="max-h-[64vh] overflow-auto rounded-xl border border-border">
          <table className="w-full min-w-[920px] border-collapse text-sm" data-testid="pie-grid">
            <thead className="sticky top-0 z-10 bg-muted">
              <tr className="[&>th]:border-b [&>th]:border-border [&>th]:px-2 [&>th]:py-2 [&>th]:text-left [&>th]:font-semibold">
                <th className="w-8">#</th>
                <th className="w-[90px]">Sr No</th>
                <th className="min-w-[160px]">Name</th>
                <th className="min-w-[120px]">Category</th>
                <th className="min-w-[110px]">Item Code</th>
                <th className="w-[120px]">Sale Price</th>
                <th className="w-[120px]">Purchase Price</th>
                <th className="w-[150px]">Store</th>
                <th className="w-[150px] text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => {
                const qq = search.trim().toLowerCase();
                if (qq && !((r.code || '').toLowerCase().includes(qq) || (r.name || '').toLowerCase().includes(qq))) return null;
                return (
                <tr key={r.key} data-testid={`pie-row-${idx}`}
                  className={`[&>td]:border-b [&>td]:border-border [&>td]:p-1 ${r._delete ? 'bg-rose-50/60 line-through opacity-70' : r.locked ? 'bg-muted/40' : r._new ? 'bg-emerald-50/50' : ''}`}>
                  <td className="px-2 text-center text-xs text-muted-foreground">{idx + 1}</td>
                  <td>{cell(r, 'sr')}</td>
                  <td>{cell(r, 'name')}</td>
                  <td>{cell(r, 'category', '', { onBlur: (e) => suggestCode(r, e.target.value) })}</td>
                  <td>{cell(r, 'code', 'font-mono uppercase', { onKeyDown: (e) => onCodeKeyDown(e, r) })}</td>
                  <td>{cell(r, 'price', 'text-right', { inputMode: 'decimal' })}</td>
                  <td>{cell(r, 'cost', 'text-right', { inputMode: 'decimal' })}</td>
                  <td>
                    {r.locked ? (
                      <span className="px-2 text-xs text-muted-foreground">{storeName(r.store_id)}</span>
                    ) : (
                      <Select value={r.store_id} onValueChange={(v) => setCell(r.key, 'store_id', v)} disabled={r._delete}>
                        <SelectTrigger className="h-9 rounded-md border-transparent" data-testid={`pie-store-${r.key}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                      </Select>
                    )}
                  </td>
                  <td className="text-center">
                    <div className="flex items-center justify-center gap-0.5">
                      <button type="button" onClick={() => insertAfter(r.key)} data-testid={`pie-insert-${r.key}`} title="Insert a row below"
                        className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-secondary hover:text-primary"><CornerDownRight className="h-4 w-4" /></button>
                      <button type="button" onClick={() => copyRow(r)} data-testid={`pie-copyrow-${r.key}`} title="Copy whole row"
                        className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground"><Copy className="h-4 w-4" /></button>
                      {r.locked ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600" title={`${r.status} — locked`}><Lock className="h-3 w-3" /> {r.status}</span>
                      ) : r._new ? (
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => removeNew(r.key)} data-testid={`pie-removenew-${r.key}`}><Trash2 className="h-4 w-4" /></Button>
                      ) : r._delete ? (
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground" onClick={() => toggleDelete(r.key)} title="Undo delete" data-testid={`pie-undodel-${r.key}`}><RotateCcw className="h-4 w-4" /></Button>
                      ) : (
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => toggleDelete(r.key)} data-testid={`pie-delete-${r.key}`}><Trash2 className="h-4 w-4" /></Button>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" className="h-10 rounded-xl" onClick={addRow} data-testid="pie-add-row"><Plus className="mr-1.5 h-4 w-4" /> Add item</Button>
          <span className="text-xs text-muted-foreground">{rows.filter((r) => r.locked).length} locked · {rows.filter((r) => r._delete).length} to delete · {rows.filter((r) => r._new).length} new</span>
          <div className="ml-auto flex gap-2">
            <Button variant="ghost" className="h-10 rounded-xl" onClick={() => onOpenChange(false)} disabled={saving} data-testid="pie-cancel">Cancel</Button>
            <Button className="h-10 rounded-xl" onClick={save} disabled={saving || !pendingCount} data-testid="pie-save">{saving ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Save changes'}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
